import { is } from "superstruct";
import UUIDClass from "uuidjs";
import { CodeableConceptModel, CodingModel } from "./structs";
import { ICodeableConcept, IHumanReadableCoding, ICoding } from "./types";

export const LOINC = "http://loinc.org" as const;
export const SCT = "http://snomed.info/sct" as const;
export const UCUM = "http://unitsofmeasure.org" as const;
export const TIRO = "http://tiro.health/fhir/sid" as const;
export const TIRO_AUTO_GENERATED =
  "http://tiro.health/fhir/CodeSystem/auto-generated";

/**
 * This regex is used to parse FSH Coding strings and matches the following format:
 * {CodeSystem}|{version string}#{code} "{display string}"
 * Examples:
 * http://snomed.info/sct|http://snomed.info/sct/900000000000207008#123456789 "Some Display"
 * http://snomed.info/sct#123456789 "Some Display"
 * http://snomed.info/sct#123456789
 * http://snomed.info/sct|http://snomed.info/sct/900000000000207008#123456789
 */
const FSH_CODING_REGEX =
  /(?<system>[^|]+)\|?(?<version>[^#]+)?#(?<code>[^\s]+)(\s+"(?<display>.+)")?/;

export class Coding implements ICoding {
  display?: string;
  code: string;
  system: string;
  version?: string;
  userSelected?: boolean;
  constructor(
    code: string,
    system: string,
    display?: string,
    version?: string,
    userSelected = false,
  ) {
    this.code = code;
    this.system = system;
    this.version = version;
    this.display = display;
    this.userSelected = userSelected;
  }

  equals(c: unknown): boolean {
    if (is(c, CodeableConceptModel)) {
      return !!c.coding.some((c) => this.equals(c));
    }
    if (
      is(c, CodingModel) &&
      this.system === c.system &&
      this.code === c.code
    ) {
      return true;
    }
    return false;
  }
  setUserSelected() {
    this.userSelected = true;
    return this;
  }
  /**
   * Parse FSH Codings following the format: {CodeSystem}|{version string}#{code} "{display string}"
   *
   * version string is optional
   * display string is optional
   *
   * @example
   * // returns { system: "http://snomed.info/sct", code: "123456789", display: "Some Display", version: "http://snomed.info/sct/900000000000207008" }
   * Coding.fromFSH("http://snomed.info/sct|http://snomed.info/sct/900000000000207008#123456789 \"Some Display\"")
   *
   * @param s - FSH Coding string
   * @returns - ICoding object
   * @throws - Error if FSH Coding string is not in the correct format
   * @see {@link https://build.fhir.org/ig/HL7/fhir-shorthand/FSH.html#coding}
   */
  static fromFSH(s: string): Coding {
    const match = s.match(FSH_CODING_REGEX);

    if (!match) {
      throw new Error(`FSH Coding string is not in the correct format: ${s}`);
    }
    const code = match.groups?.code;
    const system = match.groups?.system;
    if (!code || !system) {
      throw new Error(`FSH Coding string is not in the correct format: ${s}`);
    }
    const display = match.groups?.display;
    const version = match.groups?.version;
    return new Coding(code, system, display, version, false);
  }
  static fromObj(obj: ICoding): Coding {
    if (obj instanceof Coding) {
      return obj;
    }
    return new Coding(obj.code, obj.system, obj.display);
  }
}

export class HumanReableCoding extends Coding implements IHumanReadableCoding {
  display: string;
  constructor(
    code: string,
    system: string,
    display: string,
    version?: string,
    userSelected = false,
  ) {
    super(code, system, display, version, userSelected);
    this.display = display;
  }

  /**
   * Parse FSH Codings following the format: {CodeSystem}|{version string}#{code} "{display string}"
   *
   * version string is optional
   * display string is optional
   *
   * @example
   * // returns { system: "http://snomed.info/sct", code: "123456789", display: "Some Display", version: "http://snomed.info/sct/900000000000207008" }
   * Coding.fromFSH("http://snomed.info/sct|http://snomed.info/sct/900000000000207008#123456789 \"Some Display\"")
   *
   * @param s - FSH Coding string
   * @returns - ICoding object
   * @throws - Error if FSH Coding string is not in the correct format
   * @see {@link https://build.fhir.org/ig/HL7/fhir-shorthand/FSH.html#coding}
   */
  static fromFSH(s: string): HumanReableCoding {
    const coding = super.fromFSH(s);
    const { display } = coding;
    if (!display) {
      throw new Error(
        `FSH Coding string is not in the correct format: ${s}\nDisplay is required`,
      );
    }
    return HumanReableCoding.fromObj({ ...coding, display });
  }

  static fromObj(obj: IHumanReadableCoding): HumanReableCoding {
    if (obj instanceof HumanReableCoding) {
      return obj;
    }
    return new HumanReableCoding(obj.code, obj.system, obj.display);
  }
}

export class SCTCoding extends HumanReableCoding {
  constructor(code: string, display: string) {
    super(code, SCT, display);
  }
  static fromSCT(s: string): HumanReableCoding {
    const [code, display] = s.split("|");
    return new SCTCoding(code.trim(), display.trim());
  }
}

export class TiroCoding extends HumanReableCoding {
  constructor(
    code: string,
    display: string,
    system: string = TIRO,
    userSelected = false,
  ) {
    super(code, system, display);
  }
  static generate(display: string) {
    return new TiroCoding(UUIDClass.genV4().urn, display, TIRO_AUTO_GENERATED);
  }
}

export class UCUMCoding extends HumanReableCoding {
  constructor(code: string, display: string) {
    super(code, UCUM, display);
  }
  toUnit() {
    return {
      unit: this.display,
      code: this.code,
      system: this.system,
    };
  }
}

export class LOINCCoding extends HumanReableCoding {
  constructor(code: string, display: string) {
    super(code, LOINC, display);
  }
}

export class CodeableConcept implements ICodeableConcept {
  coding: [HumanReableCoding] | HumanReableCoding[];
  text: string;
  constructor(codings: IHumanReadableCoding[], text: string) {
    this.coding = codings.map(HumanReableCoding.fromObj);
    this.text = text;
  }
  static fromObj(obj: ICodeableConcept): CodeableConcept {
    if (obj instanceof CodeableConcept) {
      return obj;
    }
    return new CodeableConcept(obj.coding, obj.text);
  }
  static preferredCoding(
    codings: IHumanReadableCoding[],
  ): IHumanReadableCoding | undefined {
    return codings.find((c) => c.userSelected) ?? codings[0];
  }
  equals(coding: Coding): boolean {
    return this.coding.some((c) => c.equals(coding));
  }

  equivalent(concept: ICodeableConcept): boolean | null {
    const preferredOther = CodeableConcept.preferredCoding(concept.coding);
    const preferredThis = CodeableConcept.preferredCoding(this.coding);
    const isEqual =
      preferredOther &&
      preferredThis &&
      HumanReableCoding.fromObj(preferredOther).equals(preferredThis);
    return isEqual ?? null;
  }
}
